import React, { createContext, useContext, useEffect, useState } from "react";
import { AuthService } from "@/services/AuthService";
import { JWT_KEY } from "@/constants/api";
import { User } from "@/common/model/User";

type AuthProps = {
  user: User | null;
  initialized: boolean;
  fetchUser: () => Promise<void>;
  onSendCode: (email: string) => Promise<{ success: boolean; message: string }>;
  onVerifyCode: (email: string, code: string) => Promise<any>;
  onResendCode: (email: string) => Promise<{ success: boolean; message: string }>;
  onLogout: () => Promise<void>;
};

const AuthContext = createContext<Partial<AuthProps>>({
  onLogout: async () => {
    console.warn("onLogout not defined on the context");
  },
});

export function useAuth() {
  return useContext(AuthContext);
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    const initializeAuth = async () => {
      const storedUser = localStorage.getItem(JWT_KEY);

      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }

      setInitialized(true);
    };
    initializeAuth();
  }, []);

  const resetState = () => {
    setUser(null);
    AuthService.saveToken(null);
  };

  const handleSendCode = async (email: string) => {
    return AuthService.sendCode(email);
  };

  const handleVerifyCode = async (email: string, code: string) => {
    const user = await AuthService.verifyCode(email, code);
    if (!user.isadmin) {
      throw new Error("You are not admin");
    }
    setUser(user);
    AuthService.saveToken(user);

    return { success: true };
  };

  const handleResendCode = async (email: string) => {
    return AuthService.resendCode(email);
  };

  const handleLogout = async () => {
    resetState();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        initialized,
        onSendCode: handleSendCode,
        onVerifyCode: handleVerifyCode,
        onResendCode: handleResendCode,
        onLogout: handleLogout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
